import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormTextField } from '@skupreme/shared/ui/form';
import { ErrorMessageConstant } from '@utility/constants/common';
import OAuthService from '@utility/services/oauth';
import {
  ConnectIntro,
  ConnectPopup,
  failureText,
  type ConnectMarketplaceItem,
} from './ConnectPopup';

const formSchema = z.object({
  sellerId: z.string().min(1, 'Seller Id is required!'),
  sellerToken: z.string().min(1, 'Seller Token is required!'),
  apiKey: z.string().min(1, 'API Key is required!'),
});

type TargetPlusValues = z.infer<typeof formSchema>;

const EMPTY: TargetPlusValues = { sellerId: '', sellerToken: '', apiKey: '' };

type TargetPlusConnectPopupProps = {
  item: ConnectMarketplaceItem | null;
  open: boolean;
  closeModal: (success?: boolean) => void;
};

export function TargetPlusConnectPopup({
  item,
  open,
  closeModal,
}: TargetPlusConnectPopupProps) {
  const { control, handleSubmit, reset, clearErrors } =
    useForm<TargetPlusValues>({
      resolver: zodResolver(formSchema),
      defaultValues: EMPTY,
      shouldUnregister: true,
    });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const close = (connected?: boolean) => {
    reset(EMPTY);
    clearErrors();
    setSuccess('');
    setError('');
    closeModal(connected);
  };

  const onSubmit = async (data: TargetPlusValues) => {
    try {
      setSaving(true);
      setError('');
      const marketplaceId = item?.marketplace_id;
      await OAuthService.exchange({
        marketplace_id: Array.isArray(marketplaceId)
          ? marketplaceId.join()
          : marketplaceId,
        marketplace_region_id: item?.marketplace_region_id,
        marketplace_name: item?.name,
        marketplace_region: item?.alias,
        ...data,
      });
      setSuccess('Target Plus integrated successfully');

      setTimeout(() => {
        close(true);
      }, 2000);
    } catch (failure) {
      setError(
        failureText(failure, 'error', ErrorMessageConstant.defaultAPIError),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConnectPopup
      open={open}
      title={`${item?.alias ?? ''} Integration`}
      onClose={() => close()}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Submit"
      isSubmitting={saving}
      error={error}
      success={success}
    >
      <ConnectIntro logo="/images/marketplaces/target-plus.svg">
        Please paste your {item?.alias} API credentials here
      </ConnectIntro>
      <FormTextField
        control={control}
        name="sellerId"
        label="Seller Id"
        isRequired
      />
      <FormTextField
        control={control}
        name="sellerToken"
        label="Seller Token"
        isRequired
      />
      <FormTextField
        control={control}
        name="apiKey"
        label="API Key"
        isRequired
      />
    </ConnectPopup>
  );
}
